// =============================================================================
// Origin UI — src/components/ProtectedRoute.tsx
// Route wrapper that enforces authentication.
// If the user is not authenticated, triggers MSAL login redirect.
// Optionally checks for required roles (Phase 1 role model).
//
// Supported role names: HRAdmin, ITOps, SysAdmin, CreditCardAdmin, Compliance, OQTMan
// =============================================================================

import { ReactNode } from 'react';
import {
  AuthenticatedTemplate,
  UnauthenticatedTemplate,
  useMsal,
} from '@azure/msal-react';
import { loginRequest } from '@/config/authConfig';
import { useAuth } from '@/hooks/useAuth';
import type { UserRoles } from '@/hooks/useAuth';

interface ProtectedRouteProps {
  children: ReactNode;
  /** Optional: required roles (user must have at least one) */
  requiredRoles?: string[];
}

/**
 * Wraps child components so they are only rendered when the user is authenticated.
 * If the user is not authenticated, a login redirect is triggered automatically.
 * If requiredRoles are specified, checks the user's roles and shows an access denied
 * message if none match.
 */
export function ProtectedRoute({ children, requiredRoles }: ProtectedRouteProps) {
  const { instance } = useMsal();
  const { roles, rolesLoaded } = useAuth();

  const handleLogin = () => {
    instance.loginRedirect(loginRequest);
  };

  return (
    <>
      <AuthenticatedTemplate>
        {requiredRoles && requiredRoles.length > 0 ? (
          <RoleGate requiredRoles={requiredRoles} userRoles={roles} rolesLoaded={rolesLoaded}>
            {children}
          </RoleGate>
        ) : (
          children
        )}
      </AuthenticatedTemplate>

      <UnauthenticatedTemplate>
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100vh',
          fontFamily: "'DM Sans', sans-serif",
          background: '#F8FAFB',
        }}>
          <h2 style={{ color: '#0A2240', marginBottom: '16px' }}>
            Sign in to Origin
          </h2>
          <p style={{ color: '#6B7885', marginBottom: '24px' }}>
            You must be signed in with your corporate account to access this application.
          </p>
          <button
            onClick={handleLogin}
            style={{
              background: '#FF8300',
              color: '#FFFFFF',
              border: 'none',
              borderRadius: '10px',
              padding: '12px 28px',
              fontSize: '14px',
              fontWeight: 600,
              cursor: 'pointer',
              fontFamily: "'DM Sans', sans-serif",
            }}
          >
            Sign in with Microsoft
          </button>
        </div>
      </UnauthenticatedTemplate>
    </>
  );
}

/**
 * Inner component that checks role-based access.
 * Maps role name strings to the UserRoles boolean flags.
 */
function RoleGate({
  requiredRoles,
  userRoles,
  rolesLoaded,
  children,
}: {
  requiredRoles: string[];
  userRoles: UserRoles;
  rolesLoaded: boolean;
  children: ReactNode;
}) {
  // Wait for roles to load before denying access
  if (!rolesLoaded) {
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: '50vh',
        fontFamily: "'DM Sans', sans-serif",
        color: '#6B7885',
      }}>
        Loading...
      </div>
    );
  }

  // Map role names to boolean flags
  const roleMap: Record<string, boolean> = {
    // Phase 1 role names
    'HRAdmin': userRoles.isHRAdmin,
    'ITOps': userRoles.isITOps,
    'SysAdmin': userRoles.isSysAdmin,
    // Additional assigned roles
    'CreditCardAdmin': userRoles.isCreditCardAdmin,
    'Compliance': userRoles.isCompliance,
    'OQTMan': userRoles.isOQTMan,
    // Convenience aliases
    'Admin': userRoles.isAdmin,  // HRAdmin OR SysAdmin
    // Legacy role names (backward compat during transition)
    'Origin.Admin': userRoles.isAdmin,
    'Origin.ITOps': userRoles.isITOps,
    'Origin.Auditor': userRoles.isAuditor,
  };

  const hasAccess = requiredRoles.some((role) => roleMap[role]);

  if (!hasAccess) {
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100vh',
        fontFamily: "'DM Sans', sans-serif",
        background: '#F8FAFB',
      }}>
        <h2 style={{ color: '#0A2240', marginBottom: '16px' }}>
          Access Denied
        </h2>
        <p style={{ color: '#6B7885' }}>
          You do not have the required permissions to view this page.
        </p>
      </div>
    );
  }

  return <>{children}</>;
}
